/**
 * Diccionario PORTUGUÉS (pt-BR) del portal.
 *
 * Terminología de vóley de scouting (no traducción literal): sideout =
 * side-out, armador = levantador, recepción = recepção, saque = saque, armado =
 * levantamento, defensa = defesa, punta = ponteiro, opuesto = oposto, central =
 * central, líbero = líbero, rotación = rotação.
 */
import type { Messages } from './es';

export const pt: Messages = {
  nav: {
    brand: 'Scout Portal',
    federation: 'Federação',
    matches: 'Partidas',
    competitions: 'Competições',
    season: 'Temporada',
    planner: 'Rodízios',
    teams: 'Equipes',
    settings: 'Configurações',
    sameOrigin: 'mesma origem',
    connecting: 'Conectando…',
    connected: 'API conectada',
    connectedTitle: 'API v{version}',
    missingToken: 'Falta token',
    missingTokenTitle: 'A API exige Bearer token',
    down: 'API sem resposta',
  },

  common: {
    loading: 'Carregando {what}…',
    loadingData: 'dados',
    retry: 'Tentar de novo',
    refresh: 'Atualizar',
    save: 'Salvar',
    saving: 'Salvando…',
    saved: 'Salvo',
    cancel: 'Cancelar',
    all: 'Todos',
    sets: 'Sets',
    set: 'Set {set}',
    total: 'Total',
    player: 'Jogador',
    team: 'Equipe',
    unexpectedError: 'Ocorreu um erro inesperado.',
    noData: 'Sem dados',
  },

  routes: {
    unknownBefore: 'Esse endereço não existe. ',
    unknownLink: 'Ir para Partidas',
    unknownAfter: '.',
    unknownTab: 'Essa aba não existe.',
  },

  access: {
    noEvents: 'Seu perfil não vê o detalhe jogada a jogada desta partida.',
    noEventsHint:
      'Você ainda vê o resumo, a súmula e o relatório estatístico. As telas montadas a partir do log bruto (rotações, levantador, zonas e evolução) precisam da permissão «{permission}»: peça à federação.',
    noEventsTab: 'Esta tela é montada com o log jogada a jogada, que seu perfil não pode ler.',
    noEventsLive:
      'O placar ao vivo continua atualizando, mas o detalhe de cada jogada não: seu perfil não pode ler o log.',
    noScoreboard: 'O placar por sets é reconstruído do log, que seu perfil não pode ler.',
    goToSummary: 'Ver o resumo',
  },

  matches: {
    title: 'Partidas',
    searchPlaceholder: 'Buscar por nome ou id…',
    searchLabel: 'Buscar partidas',
    loadingWhat: 'partidas',
    empty:
      'Ainda não há partidas no arquivo. Assim que um posto de captura sincronizar, elas aparecem aqui.',
    noMatchesFor: 'Nenhuma partida corresponde a «{query}».',
    colMatch: 'Partida',
    colRules: 'Regulamento',
    colEvents: 'Eventos',
    colLastSeq: 'Últ. seq',
    colUpdated: 'Atualizado',
    colActions: 'Ações',
    scoresheet: 'Súmula',
    scoresheetTitle: 'Súmula oficial da partida, pronta para imprimir e assinar.',
    report: 'Relatório',
    reportTitle: 'Relatório estatístico: fundamentos, side-out/break e jogadores.',
    dvw: '.dvw',
    mesa: 'Mesa',
    mesaTitle: 'Mesa de controle da súmula: oficiais, challenge e assinaturas.',
  },

  /** Convenção de eficiência ativa (escolhida em Configurações, mostrada em cada tela). */
  efficiency: {
    chip: 'Convenção: {name}',
    chipTitle:
      'Com quais fórmulas se calculam eficiência e positividade em TODAS as abas de estatística. Muda em Configurações.',
  },

  /** Filtro "One Rally Before": condicionar pelo rally anterior. */
  prevRally: {
    filterLabel: 'Rally anterior',
    none: 'Sem condição',
    teamLabel: 'visto de',
    activeHint:
      'Entram só os rallies cujo rally ANTERIOR (do mesmo set) cumpre a condição. Vale para Resumo, Levantador, Zonas e Clipes; Rodízios e Evolução não usam este recorte, e os documentos imprimíveis também não.',
    preset_afterOwnPoint: 'após ponto próprio',
    preset_afterRivalPoint: 'após ponto do rival',
    preset_afterOwnBreak: 'após ponto de break próprio',
    preset_afterRivalBreak: 'após ponto de break do rival',
    preset_afterOwnReceptionError: 'após erro de recepção próprio',
    preset_afterOwnAttackError: 'após erro de ataque próprio',
    preset_afterOwnAce: 'após ace próprio',
  },

  match: {
    loadingWhat: 'a partida',
    loadingReport: 'o relatório',
    printable: 'Súmula para impressão',
    report: 'Relatório estatístico',
    downloadDvw: 'Baixar .dvw',
    noClosedSets: 'Nenhum set fechado ainda.',
    tabSummary: 'Resumo',
    tabRotations: 'Rotações',
    tabSetter: 'Levantador',
    tabZones: 'Zonas',
    tabClips: 'Clipes',
    tabEvolution: 'Evolução',
    tabLive: 'Ao vivo',
  },

  summary: {
    noPoints: 'Nenhum ponto registrado.',
    pointBy: 'Ponto de',
    count: 'Qtd.',
    percent: '%',
    bySkill: 'Por fundamento',
    pointsByReason: 'Pontos por origem',
    players: 'Jogadores',
    sideout: 'Side-out {pct}',
    sideoutTitle: 'Pontos ganhos recebendo / rallies recebidos',
    breakPoint: 'Break {pct}',
    breakTitle: 'Pontos ganhos sacando / rallies sacados',
    pointsAttribution: 'Atribuição de pontos',
    pointsOrigin: 'De onde saiu',
    pointsMeans: 'O que quer dizer',
    pointsScoreboard: 'Pontos do placar',
    pointsScoreboardHint: 'O que o placar diz no recorte escolhido.',
    pointsByPlayer: 'Ação própria com camisa',
    pointsByPlayerHint: 'Ace, ponto de ataque ou de bloqueio, com o autor codificado.',
    pointsUnassigned: 'Ação própria sem camisa',
    pointsUnassignedHint: 'O ponto é da equipe, mas o log não diz de quem.',
    pointsUnassignedNote:
      'A linha de total soma {count} ponto(s) de ação própria sem camisa, que não aparecem em nenhuma linha de jogador.',
    pointsOpponentErrors: 'Erros do adversário',
    pointsOpponentErrorsHint:
      'Foram dados pelo adversário: não têm autor próprio e não se inventa nenhum.',
    pointsIdentity: 'Fecha: {byPlayer} + {unassigned} + {opponentErrors} = {total} pontos do placar.',
    pointsMismatch:
      'O detalhamento não fecha com o placar. É um bug do relatório, não da partida: avise antes de usar estes números.',
    pointsByPlayerTitle: 'Pontos por jogador',
    noScorers: 'Nenhum jogador marcou pontos por ação própria no recorte escolhido.',
    noPointsBreakdown:
      'Esta API não publica o detalhamento de atribuição de pontos (é uma versão anterior do relatório).',
    player: 'Jogador',
    ace: 'Ace',
    aceTitle: 'Pontos de saque',
    kill: 'Ataque',
    killTitle: 'Pontos de ataque',
    block: 'Bloqueio',
    blockTitle: 'Pontos de bloqueio',
    pointsChip: '{total} pts · {byPlayer} com autor',
    pointsChipTitle: 'Pontos do placar e quantos têm jogador codificado',
  },

  skills: {
    empty: 'Nenhum fundamento codificado.',
    noPlayers: 'Nenhum jogador com ações.',
    skill: 'Fundamento',
    total: 'Tot',
    perfect: '#',
    perfectTitle: 'Perfeitos',
    errors: '=',
    errorsTitle: 'Erros',
    efficiency: 'Ef%',
    efficiencyTitle: 'Eficiência',
    positivity: 'Pos%',
    positivityTitle: 'Perfeitos / positividade na recepção',
    matchesPlayed: 'PJ',
    points: 'Pts',
    pointsTitle: 'Pontos marcados',
    names: {
      serve: 'Saque',
      reception: 'Recepção',
      set: 'Levantamento',
      attack: 'Ataque',
      block: 'Bloqueio',
      dig: 'Defesa',
      freeball: 'Bola livre',
    },
    reasons: {
      attack: 'Ataque',
      block: 'Bloqueio',
      serve: 'Saque',
      opponent_error: 'Erro do adversário',
      sanction: 'Sanção',
      delay_penalty: 'Penalização por atraso',
      illegal_substitution: 'Substituição ilegal',
      position_fault: 'Falta de posição',
      challenge_adjustment: 'Ajuste por challenge',
      forfeit: 'W.O.',
      other: 'Outro',
    },
  },

  rotations: {
    intro:
      'Rotação = posição do levantador (convenção DataVolley). O rally é contado na rotação em que foi jogado; a dupla substituição mantém a vaga do levantador.',
    noLineup:
      'O log não traz a formação inicial (evento lineup_set), então não dá para saber em que rotação cada rally foi jogado.',
    noSetter:
      'Há formação, mas não é possível identificar o levantador (falta setterPosition no lineup e nenhum jogador do elenco declara a função de levantador).',
    untracked: 'Rotação não reconstruível.',
    unassignedSuffix: ' ({count} rallies sem rotação atribuída)',
    unassignedChip: '{count} sem atribuir',
    unassignedTitle: 'Rallies jogados antes de a formação ser conhecida',
    sideoutByRotation: 'Side-out por rotação',
    colRotation: 'Rot.',
    colRallies: 'Rallies',
    colReceived: 'Rec',
    colReceivedTitle: 'Rallies recebidos',
    colSideout: 'Side-out%',
    colServed: 'Saq',
    colServedTitle: 'Rallies sacados',
    colBreak: 'Break%',
    colNet: 'Saldo',
    colNetTitle: 'Pontos ganhos − perdidos',
    barTitle: '{label}: side-out {pct} ({won}/{total})',
  },

  setter: {
    intro:
      'O destino é identificado pela combinação do ataque; se ela não estiver codificada usa-se a zona de saída e, em último caso, a de chegada.',
    attacksSet: '{count} ataques levantados',
    empty:
      'Não há ataques associados a uma recepção codificada: sem recepções no log esta visão fica vazia (não é um erro).',
    receptionHeading: 'Recepção {grade} ({name}) — {count} ataques',
    gradePerfect: 'perfeita',
    gradePositive: 'positiva',
    gradeNeutral: 'neutra',
    gradeNegative: 'ruim',
    colTarget: 'Destino',
    colSource: 'Origem do dado',
    colAttacks: 'Ataques',
    colPercent: '%',
    colEfficiency: 'Ef%',
    uncoded: 'sem codificar',
    sourceCombo: 'combinação',
    sourceStartZone: 'zona de saída',
    sourceEndZone: 'zona de chegada',
    sourceUnknown: '—',
    segmentTitle: '{target}: {count} ataques ({pct}), eficiência {efficiency}',
    receptionCardTitle: '{team} · recepção → ponto',
    receptionCardHint:
      'Conversão = pontos ganhos sobre os rallies COM recepção codificada. Não é o Side-out% do resumo e das rotações, que corre sobre todos os rallies recebidos: por isso os dois números podem não coincidir.',
    noReceptions: 'Nenhuma recepção codificada.',
    colGrade: 'Grau',
    colRallies: 'Rallies',
    colSideout: 'Pontos',
    colSideoutPct: 'Conversão%',
    noSetAttacks: 'Nenhum ataque levantado com esta recepção.',
    phaseFilter: 'Fase',
    phaseAll: 'As duas fases',
    phaseSideout: 'Primeira bola',
    phaseTransition: 'Contra-ataque',
    phaseAllHint:
      'Side-out e transição juntos: a distribuição completa do levantador, condicionada ao grau da recepção.',
    phaseSideoutHint:
      'Primeira bola: o ataque que sai da recepção, com todas as opções abertas e o bloqueio adversário ainda sem ler.',
    phaseTransitionHint:
      'Contra-ataque rearmado após defesa própria, onde costuma sobrar uma única opção. Inclui os rallies que a equipe sacou.',
    phaseTotal: '{count} ataques nesta fase',
    phaseWithReception: '{count} com recepção levantável',
    phaseWithReceptionTitle:
      'Ataques de rallies com recepção própria de grau #, +, ! ou −: são os únicos que entram nas tabelas por grau',
    phaseWithoutReception: '{count} sem recepção própria',
    phaseWithoutReceptionTitle:
      'Ataques de rallies que a equipe sacou, ou com recepção / ou =: existem, mas não há grau para condicionar',
    phaseEmpty: 'Nenhum ataque nesta fase.',
    phaseNoGrades:
      'Os ataques desta fase não saem de uma recepção codificada de grau levantável, então não há tabela por grau.',
    phaseAllTargets: 'Todos os destinos da fase',
  },

  clips: {
    filterCard: 'Filtro',
    intro:
      'Cada toque codificado com timecode do VAR é um clipe. Escolha o recorte e peça os vídeos: quem corta é o servidor de replay do ginásio, não o portal.',
    team: 'Equipe',
    bothTeams: 'As duas',
    player: 'Camisa',
    allPlayers: 'Todas',
    playerNeedsTeam: 'Escolha uma equipe para filtrar por camisa.',
    skill: 'Fundamento',
    allSkills: 'Todos',
    skill_serve: 'Saque',
    skill_reception: 'Recepção',
    skill_set: 'Levantamento',
    skill_attack: 'Ataque',
    skill_block: 'Bloqueio',
    skill_dig: 'Defesa',
    skill_freeball: 'Freeball',
    evaluation: 'Avaliação',
    allEvaluations: 'Todas',
    rotation: 'Rotação',
    allRotations: 'Todas',
    rotationOption: 'P{position} (levantador)',
    setsHint: 'O recorte de sets é o seletor de cima, o mesmo de todas as abas.',
    camerasCard: 'Câmeras',
    camerasWhat: 'as câmeras do VAR',
    camerasError: 'Não deu para ler o catálogo de câmeras do VAR ({reason}).',
    noCameras: 'O VAR não declara nenhuma câmera.',
    noVar: 'Não há servidor de replay configurado neste navegador.',
    noVarHint:
      'A lista de cortes funciona igual (rótulo, timecode e duração): falta quem produza o arquivo. Configure em Ajustes → Servidor de replay (VAR), com a URL do ginásio.',
    listCard: 'Clipes',
    loadingWhat: 'os clipes',
    count: '{count} clipe(s)',
    skipped: '{count} toque(s) sem timecode: não há clipe possível.',
    files: '{files} arquivo(s) — um corte por cada uma das {cameras} câmera(s) escolhidas.',
    requestAll: 'Pedir os {count} clipes',
    jobsSummary: '{done} pronto(s) · {working} em andamento · {failed} com erro',
    emptyForFilter: 'Nenhum clipe para este filtro.',
    colClip: 'Clipe',
    colSet: 'Set',
    colTimecode: 'Timecode',
    colDuration: 'Duração',
    colState: 'Estado',
    seconds: '{seconds} s',
    timecodeTitle: 'videoNs {ns} (relógio mestre do VAR)',
    request: 'Pedir clipe',
    download: 'Baixar',
    failed: 'Falhou',
    state_pending: 'Pedindo…',
    state_queued: 'Na fila',
    state_running: 'Cortando',
  },

  zones: {
    intro:
      '«s/d» agrupa os toques sem essa coordenada codificada: não são descartados, para o total fechar. A cor é a eficiência observada na própria matriz e o número exato vai escrito em cada célula.',
    skillFilter: 'Fundamento',
    skillAttack: 'Ataque',
    skillServe: 'Saque',
    skillReception: 'Recepção',
    introAttack: 'Linhas = zona de saída do ataque, colunas = zona de chegada.',
    introServe: 'Linhas = zona de onde se sacou, colunas = zona para onde o saque foi.',
    introReception:
      'Linhas = zona de onde veio o saque, colunas = zona onde se recebeu. Os «perfeitos» aqui são a positividade (# + +), que é o que se olha em quadra.',
    playerFilter: 'Jogador',
    serverFilter: 'Sacador',
    receiverFilter: 'Passador',
    wholeTeam: 'Equipe toda',
    emptyTeam: 'Nenhum ataque codificado nos sets escolhidos.',
    emptyPlayer: 'O #{player} não tem ataques codificados nos sets escolhidos.',
    emptyTeamServe: 'Nenhum saque codificado nos sets escolhidos.',
    emptyPlayerServe: 'O #{player} não tem saques codificados nos sets escolhidos.',
    emptyTeamReception: 'Nenhuma recepção codificada nos sets escolhidos.',
    emptyPlayerReception: 'O #{player} não tem recepções codificadas nos sets escolhidos.',
    attacks: '{count} ataques',
    serves: '{count} saques',
    receptions: '{count} recepções',
    kills: '{count} pontos',
    aces: '{count} aces',
    perfectReceptions: '{count} perfeitas',
    killPct: 'Ponto {pct}',
    acePct: 'Ace {pct}',
    positivityPct: 'Positividade {pct}',
    errors: '{count} erros',
    blocked: '{count} bloqueados',
    efficiency: 'Eficiência {pct}',
    cellTitle:
      'Saída {startZone} → chegada {endZone}: {total} ataques, {kills} pontos, {errors} erros, {blocked} bloqueados · eficiência {efficiency}',
    cellTitleServe:
      'Saque de {startZone} → zona {endZone}: {total} saques, {kills} aces, {errors} erros, {blocked} devolvidos · eficiência {efficiency}',
    cellTitleReception:
      'Saque de {startZone} → recebido em {endZone}: {total} recepções, {kills} perfeitas, {errors} erros, {blocked} devolvidas · eficiência {efficiency}',
    noZoneData: 's/d',
    emptyMatrix: 'Nenhum toque com zona codificada.',
    axisRow: 'Saída',
    axisCol: 'Chegada',
    axisRowServe: 'Saque de',
    axisColServe: 'Para',
    axisRowReception: 'Saque de',
    axisColReception: 'Recebido em',
    matrixLabel: 'Matriz {rowLabel} × {colLabel}',
    legend: 'Eficiência',
  },

  evolution: {
    empty: 'Ainda não há pontos no log para desenhar a evolução.',
    emptySet: 'O set {set} não tem pontos registrados.',
    chartLabel: 'Evolução do set {set}',
    chartTitle: 'Set {set}: {homeName} {home} – {awayName} {away}',
    firstRally: 'rally 1',
    lastRally: 'rally {count}',
    barChartLabel: 'Gráfico de barras',
    distributionLabel: 'Distribuição',
  },

  live: {
    statusIdle: 'Sem conexão',
    statusConnecting: 'Conectando…',
    statusOpen: 'Ao vivo',
    statusReconnecting: 'Reconectando…',
    statusClosed: 'Desconectado',
    statusError: 'Erro de conexão',
    sets: 'Sets {home}–{away}',
    setClosed: 'set fechado',
    lastEvents: 'Últimos eventos',
    empty:
      'Ainda não entrou nenhum evento. Se a partida está em jogo, o posto de captura precisa estar sincronizando com esta API.',
    openFailed: 'A API respondeu {status} ao abrir o ao vivo.',
    noFetch: 'Este ambiente não tem fetch.',
    noStreamBody: 'O navegador não expõe o corpo do stream.',
  },

  /** Console de partida (Fase D). Lê-se a um metro: textos curtos, uma ideia cada. */
  bench: {
    views: {
      banco: 'Banco',
      mesa: 'Mesa',
      arbitro: 'Árbitro',
      espejo: 'Supervisor',
      tablero: 'Placar',
    },
    access: {
      title: 'Console de partida',
      explain: 'Escaneie o QR que a mesa mostra para entrar nesta partida.',
      hint: 'A credencial vale só para essa partida e com a função atribuída.',
      loading: 'Carregando a partida…',
      failed: 'A credencial não pôde ser trocada. Peça à mesa que emita outra.',
    },
    link: {
      offline: 'SEM REDE',
      cold: 'SEM DADOS',
      keepPlaying: 'A partida continua. Sem tablet, o pedido oficial é o sinal.',
      lastData: 'último dado há {seconds} s',
      retry: 'Tentar de novo',
    },
    prefs: {
      glareOn: 'Alto contraste',
      glareOff: 'Contraste normal',
    },
    score: {
      serving: 'Saca',
      servingShort: 'SAQ',
      closed: 'SÚMULA FECHADA',
      timeoutsLeft: 'tempos',
      subsLeft: 'substituições',
      challengesLeft: 'challenges',
      timeoutsLeft2: 'restam {n}',
      challengesLeft2: 'restam {n}',
      exceptional: '{count} excepcional(is)',
    },
    court: {
      title: 'Quadra · {team}',
      flip: 'Girar quadra',
      unflip: 'Voltar',
      noLineup: 'Ainda não há escalação carregada para este set.',
      net: 'REDE',
    },
    rallies: {
      title: 'Rallies do set {set}',
      empty: 'Nenhum ponto jogado ainda.',
      servedBy: 'saque de {team}',
      unknownServer: 'saque não registrado',
      jump: 'Ver {tc}',
      noTimecode: 'sem vídeo',
    },
    numbers: {
      title: 'Números da partida',
      loading: 'Calculando…',
      unavailable: 'Os números não estão disponíveis com esta credencial.',
      sideout: 'Sideout',
      break: 'Break',
      attack: 'Ataque',
      reception: 'Recepção +',
      topScorers: 'Pontos',
    },
    request: {
      title: 'Pedir',
      timeout: 'Tempo',
      challenge: 'Challenge',
      substitution: 'Substituição',
      sending: 'Enviando…',
      noNetwork: 'Não foi possível enviar',
      useSignal: 'Peça com o sinal ao 2º árbitro: sem rede, o pedido oficial é o físico.',
      underReview: 'Há uma revisão em curso: não se pedem tempos nem substituições.',
      subsUsed: '{used} de {limit}',
      subsWarn: 'Restam poucas substituições neste set.',
      noSubsLeft:
        'Sem substituições neste set. A substituição excepcional (regra 15.7) pede-se com o sinal e o 2º árbitro autoriza.',
      whoLeaves: 'Quem sai?',
      whoEnters: 'Quem entra?',
      cannotLeave: 'Já saiu e voltou neste set.',
      pairLocked: 'Só pode entrar o #{player}: foi quem o substituiu (regra 15.6).',
      noCandidates: 'Ninguém disponível no banco.',
    },
    lineup: {
      title: 'Escalação do set {set}',
      official: 'A escalação enviada pelo tablet é oficial (regra 5.2.3.1).',
      send: 'Enviar escalação',
      clear: 'Limpar',
      useSheet: 'Sem rede: entregue a ficha de posições em papel.',
    },
    queue: {
      title: 'Pedidos',
      empty: 'Não há pedidos.',
      pending: '{n} sem atender',
      ago: 'há {seconds} s',
      by: 'pedido de {who}',
      accept: 'Aceitar',
      reject: 'Recusar',
      cancel: 'Retirar',
      accepted: 'Aceito por {who}',
      rejected: 'Recusado: {reason}',
      cancelled: 'Retirado pelo banco.',
      stale: 'Venceu: o jogo seguiu. Se couber, registre à mão.',
      subDetail: 'sai #{out}, entra #{in}',
      kinds: {
        timeout: 'Tempo',
        substitution: 'Substituição',
        challenge: 'Challenge',
        lineup: 'Escalação',
      },
    },
  },

  mesa: {
    title: 'Mesa de controle',
    tabs: {
      oficiales: 'Oficiais',
      challenge: 'Challenge',
      acta: 'Súmula',
    },
    loadingWhat: 'a súmula',
    closedBanner:
      'A súmula está fechada e assinada: o que chegar agora precisa de uma retificação autorizada.',
    phase: {
      BEFORE_MATCH: 'Antes da partida',
      AFTER_TOSS: 'Sorteio feito',
      DURING_MATCH: 'Partida em andamento',
      AFTER_MATCH: 'Partida encerrada',
      CLOSED: 'Súmula fechada',
    },
    team: {
      home: 'mandante',
      away: 'visitante',
    },
    roles: {
      referee1: '1º árbitro',
      referee2: '2º árbitro',
      challenge_referee: 'Árbitro de challenge',
      reserve_referee: 'Árbitro reserva',
      scorer: 'Apontador',
      assistant_scorer: 'Apontador assistente',
      line_judge: 'Juiz de linha',
      team_delegate: 'Delegado de banco',
      team_captain: 'Capitão',
      supervisor: 'Supervisor',
      replay_operator: 'Operador de replay',
      scoreboard: 'Placar do estádio',
      statistician: 'Estatístico',
      system: 'Sistema',
    },
    oficiales: {
      title: 'Equipe de arbitragem',
      explain:
        'O que você carrega aqui vai para a súmula (caixa APPROVAL) e define quem pode assinar o quê.',
      empty: 'A equipe de arbitragem ainda não foi carregada.',
      role: 'Função',
      name: 'Nome',
      country: 'País (ISO3)',
      license: 'Registro',
      add: 'Adicionar oficial',
      remove: 'Remover',
      save: 'Registrar na súmula',
      saved: 'Equipe de arbitragem registrada na súmula.',
      nameMissing: 'Todo oficial precisa de nome.',
      credsTitle: 'Credenciais da partida (QR+PIN)',
      credsExplain:
        'Uma por pessoa presente. O link abre o console com a sua função; o PIN é ditado ao titular e é o que assina: o link sem o PIN opera, não assina.',
      credsEmpty: 'Nenhuma credencial emitida ainda.',
      holder: 'Titular',
      side: 'Banco',
      sideNone: 'Sem banco',
      issue: 'Emitir credencial',
      holderMissing: 'Falta o nome do titular.',
      revoke: 'Revogar',
      revoked: 'Revogada',
      confirmRevoke: 'Revogar a credencial de {holder}? Para de autenticar na hora.',
      kindAccount: 'conta do cadastro',
      kindCredential: 'QR+PIN',
      issuedTitle: 'Credencial emitida — isto aparece UMA única vez',
      issuedLink: 'Link de acesso (é o conteúdo do QR)',
      issuedPin: 'PIN — dite ao titular, não deixe à vista',
      copyLink: 'Copiar link',
      copied: 'Copiado.',
      qrPending:
        'Peça ao titular para escanear com a câmera do tablet: entra direto no console com sua função. Sem câmera, o link também pode ser copiado ou digitado.',
      issuedDone: 'Pronto, entreguei',
    },
    challenge: {
      title: 'Challenge em andamento',
      none: 'Nenhuma revisão em andamento.',
      windowOpen: 'A janela do último ponto está aberta: ainda dá para desafiar (art. 7).',
      remainingNow: 'Challenges restantes — {home} mandante · {away} visitante',
      requestedBy: 'Desafia a equipe {team}',
      requestedByRef: 'Revisão de ofício do 1º árbitro (art. 12)',
      status: {
        AWAITING_REASON: 'Aguardando o motivo',
        UNDER_REVIEW: 'Em revisão',
      },
      setScore: 'Set {set} · {score} (o número do solicitante primeiro)',
      at: 'Pedido aos {tc} do vídeo',
      claimed: 'Motivo declarado: {fault}',
      claimedNone: 'Sem motivo declarado (art. 8: escolhe-se num segundo passo)',
      pickFault: 'Registrar o motivo que o banco escolheu',
      faults: {
        BALL_IN_OUT: 'Bola dentro/fora',
        BLOCK_TOUCH: 'Toque no bloqueio',
        NET_FAULT: 'Falta de rede',
        ANTENNA_TOUCH: 'Toque na antena',
        FOOT_FAULT: 'Falta de pé',
        FLOOR_TOUCH: 'Toque no chão',
        LAST_TOUCH: 'Último toque',
        REACH_OVER: 'Invasão por cima da rede',
      },
      resolveTitle: 'Resultado da revisão',
      onlyUnsuccessful:
        'O pedido nunca declarou a falta: a única resolução possível é «incorreto» (art. 24).',
      outcomes: {
        SUCCESSFUL: 'Correto',
        UNSUCCESSFUL: 'Incorreto',
        INCONCLUSIVE: 'Inconclusivo',
        DECLINED_UNNECESSARY: 'Desnecessário',
        FOOTAGE_UNAVAILABLE: 'Sem vídeo',
      },
      outcomeHints: {
        SUCCESSFUL: 'A reclamação estava certa. Retém o challenge (art. 2).',
        UNSUCCESSFUL: 'A reclamação estava errada. PERDE o challenge (art. 11).',
        INCONCLUSIVE: 'O vídeo não decide. Retém o challenge (art. 2).',
        DECLINED_UNNECESSARY: 'Caiu sozinho: ganhou o rally mesmo assim ou o rival admitiu (art. 14). Retém.',
        FOOTAGE_UNAVAILABLE: 'Não houve imagem para revisar. Retém o challenge.',
      },
      retained: 'A equipe RETÉM o challenge.',
      lost: 'A equipe PERDE o challenge.',
      observed: 'Falta observada (prevalece a primeira observada, mesmo que não seja a desafiada — art. 13)',
      report: 'Relatório do árbitro de challenge (opcional)',
      scoreTitle: 'Reconciliação do placar',
      scoreExplain:
        'Não é um desfazer genérico: INVERT muda a equipe do saque e DELETE devolve o saque ao mesmo sacador. Errar aqui quebra o rodízio do resto do set.',
      actions: {
        CONFIRM: 'CONFIRMAR',
        INVERT: 'INVERTER o ponto',
        DELETE_LAST_POINT: 'ANULAR o último ponto',
        REPLAY: 'REPETIR o rally',
      },
      actionHints: {
        CONFIRM: 'O placar fica como está.',
        INVERT: 'O ponto passa para a outra equipe: muda quem saca.',
        DELETE_LAST_POINT: 'O ponto é anulado: o saque volta ao mesmo sacador.',
        REPLAY: 'O ponto é anulado e o rally se repete: saca a mesma equipe.',
      },
      planConfirm: 'O placar não muda.',
      planInvert: 'O último ponto é invertido: passa para a equipe {team}.',
      planDelete: 'O último ponto é anulado: o saque volta ao mesmo sacador.',
      planReplay: 'O ponto é anulado e o rally se repete.',
      afterRemaining: 'Depois disto restam: {home} ao mandante · {away} ao visitante.',
      noPoint: 'O log não tem um ponto vigente para reconciliar.',
      confirm: 'Registrar a resolução',
      resolvedOk:
        'Challenge resolvido e placar reconciliado num único lote. Checkpoint do 2º árbitro: súmula, placar e rodízio antes de autorizar o saque.',
      reviewSeconds: 'Revisão em andamento: {seconds} s (sem limite regulamentar)',
      logTitle: 'Challenges da partida',
      logEmpty: 'Nenhum ainda.',
      logRetained: 'retido',
      logLost: 'perdido',
    },
    acta: {
      title: 'Assinaturas e fechamento',
      rosterTitle: 'Verificação de elenco (regra 4.1.3)',
      rosterExplain:
        'Com as assinaturas prévias de cada banco, o elenco daquela equipe fica congelado: depois disto ninguém mais entra na lista.',
      rosterLocked: 'Elenco CONGELADO',
      rosterOpen: 'Elenco ainda aberto',
      players: '{count} jogadores na lista',
      signaturesTitle: 'Assinaturas do fechamento — instrução 4.10, nesta ordem',
      scorerFirst:
        'O apontador assina primeiro: a sua assinatura congela o digest e as demais assinam ESSE documento (regra 27.2.3.3).',
      signedBy: 'Assinou {name}',
      pendingSlot: 'Falta assinar',
      nextBadge: 'PRÓXIMA ASSINATURA',
      noHolder: 'Sem credencial: emita em Oficiais.',
      digestMismatch: 'Esta assinatura NÃO coincide com o digest congelado! A súmula mudou entre assinaturas.',
      sign: 'Assinar',
      signAs: 'Assina {name} ({role})',
      secretPin: 'PIN da credencial — digitado pelo titular, não pela mesa',
      secretPassword: 'Senha da conta — digitada pelo titular, não pela mesa',
      needsToken:
        'Esta credencial foi emitida em outro navegador e a mesa não guarda o token: reemita em Oficiais ou que assine do próprio dispositivo.',
      accountHint:
        'Conta do cadastro: assina com a própria senha. Vale se a sessão ativa desta mesa for a dela, ou da própria sessão.',
      signedOk: 'Assinatura registrada e atada ao digest do documento.',
      frozenDigest: 'Digest congelado: {digest}',
      closeTitle: 'Fechamento e entrega',
      closeExplain:
        'O fechamento congela o documento assinado: depois disto o motor rejeita qualquer acréscimo sem uma retificação autorizada (§8.4).',
      outcomeLabel: 'Resultado da partida',
      outcomes: {
        NORMAL: 'Normal',
        DEFAULT: 'W.O. (0-3, regra 6.4)',
        INCOMPLETE: 'Equipe incompleta (mantém seus pontos)',
        WITHDRAWN: 'Retirada',
      },
      deliveriesLabel: 'Entrega do documento assinado',
      deliveries: {
        FEDERATION: 'Federação',
        TEAM_A: 'Equipe A',
        TEAM_B: 'Equipe B',
        ORGANISER: 'Comitê organizador',
        SUPERVISOR: 'Supervisor',
      },
      close: 'FECHAR A SÚMULA',
      confirmClose:
        'Fechar a súmula? Congela o documento assinado: depois só se toca com uma retificação autorizada.',
      closeUnsigned: 'Sem nenhuma assinatura de fechamento não dá para fechar: o apontador assina primeiro.',
      closedOk: 'Súmula fechada e entregue.',
      closedDigest: 'Digest do documento fechado: {digest}',
      printable: 'Abrir a súmula',
    },
  },

  season: {
    title: 'Temporada',
    includedMatches: 'Partidas incluídas',
    apply: 'Aplicar',
    noMatches: 'Ainda não há partidas no arquivo.',
    loadingWhat: 'partidas',
    loadingTotals: 'o acumulado',
    allSeason: 'Sem seleção = a temporada toda.',
    selectedCount: '{count} partida(s) selecionada(s).',
    totals: 'Acumulado · {count} partida(s)',
    setsChip: 'Sets: {sets}',
    setsAll: 'todos',
    colTeam: 'Equipe',
    colMatches: 'PJ',
    colSideout: 'Side-out%',
    colBreak: 'Break%',
    colReceived: 'Rec',
    colReceivedTitle: 'Rallies recebidos',
    colServed: 'Saq',
    colServedTitle: 'Rallies sacados',
    topEfficiency: 'Top eficiência de ataque',
    minAttacks: 'Mínimo de {count} ataques no acumulado.',
    topPoints: 'Top pontos',
    unitAttacks: 'ataques',
    unitMatches: 'PJ',
    bySkill: 'Por fundamento',
    players: 'Jogadores',
    noRanked: 'Nenhum jogador se qualifica ainda.',
    colValue: 'Valor',
  },

  /** Rotation Planner (#/planner): a lousa de rodízios. */
  planner: {
    title: 'Rotation Planner',
    intro:
      'Sexteto inicial → os 6 rodízios: quem fica em cada zona, quais sobreposições a regra exige e como cada rodízio rendeu quando há dados.',
    sourceCard: 'Dados da partida (opcional)',
    sourceIntro:
      'Escolha uma partida para pré-carregar o sexteto titular, pôr nomes nas camisas e cruzar cada rodízio com sua estatística real.',
    matchLabel: 'Partida',
    noMatch: 'Sem partida (lousa vazia)',
    teamLabel: 'Equipe',
    prefill: 'Carregar sexteto titular',
    seasonToggle: 'Acumular a temporada inteira da equipe',
    noEvents:
      'Seu papel não pode ler o log de eventos, então não há pré-carga nem estatística por rodízio.',
    noEventsHint: 'A lousa continua funcionando: digite as camisas à mão.',
    loadingSeason: 'a temporada da equipe',
    seasonEmpty:
      'Nenhuma partida da temporada tem rodízio reconstruível para esta equipe (falta line-up ou levantador identificável).',
    lineupCard: 'Sexteto inicial',
    lineupIntro:
      'Uma camisa por posição. P1 é o zagueiro direito (quem saca); P2–P3–P4 são a rede.',
    slotLabel: 'Camisa na P{position}',
    setterLabel: 'Levantador',
    noSetter: 'Sem levantador',
    duplicates: 'Camisas repetidas: {numbers}. Assim nenhum rodízio é legal.',
    incomplete: 'O sexteto está incompleto: os rodízios são desenhados mesmo assim, com buracos.',
    noSetterHint:
      'Sem levantador escolhido os rodízios não são numerados (P1..P6 é a posição do levantador) nem cruzados com a estatística.',
    empty: 'Digite o sexteto (ou pré-carregue de uma partida) e os 6 rodízios aparecem aqui.',
    untracked:
      'Esta equipe não tem rodízio reconstruível na partida escolhida (sem line-up ou sem levantador identificável): os cartões saem sem estatística.',
    rotationTitle: 'Rodízio {order}',
    rotationTitleSetter: 'Rodízio {order} — levantador na P{position}',
    ralliesTitle: 'Rallies jogados neste rodízio nos dados escolhidos.',
    rallies: '{count} rallies',
    sideoutTitle: 'Pontos ganhos recebendo / rallies recebidos, neste rodízio.',
    sideout: 'SO {pct}',
    breakTitle: 'Pontos ganhos sacando / rallies sacados, neste rodízio.',
    breakPoint: 'BP {pct}',
    netTitle: 'Pontos ganhos menos perdidos neste rodízio.',
    setterChip: 'L',
    overlapsTitle: 'Sobreposições a cuidar (regra 7.4)',
    overlapFrontBack:
      '{back} (Z{backZone}) não pode passar à frente de {front} (Z{frontZone}) até o saque.',
    overlapLeftRight:
      '{left} (Z{leftZone}) tem que ficar à esquerda de {right} (Z{rightZone}) até o saque.',
    emptySlot: 'buraco',
  },

  competition: {
    // --- Listado -------------------------------------------------------------
    title: 'Competições',
    subtitle: 'Torneios, tabela de jogos e classificação da federação.',
    loadingWhat: 'competições',
    empty:
      'Ainda não há competições. Uma competição agrupa fases (turno regular, grupos, playoff), sua tabela de jogos e sua classificação — e liga as partidas que já estão no arquivo.',
    colName: 'Competição',
    colSeason: 'Temporada',
    colStatus: 'Situação',
    colTeams: 'Equipes',
    colPhases: 'Fases',
    colUpdated: 'Atualizada',
    open: 'Abrir',
    statusDraft: 'Rascunho',
    statusActive: 'Em andamento',
    statusFinished: 'Encerrada',

    // --- Ficha ---------------------------------------------------------------
    tabFixture: 'Jogos',
    tabStandings: 'Classificação',
    tabInfo: 'Ficha',
    notFound: 'Essa competição não existe.',
    backToList: 'Voltar às competições',
    loadingCompetition: 'a competição',
    phase: 'Fase',
    group: 'Grupo',
    allPhases: 'Todas as fases',
    teamsTitle: 'Equipes participantes',
    phasesTitle: 'Fases',
    phaseKindRegular: 'Todos contra todos',
    phaseKindGroups: 'Grupos',
    phaseKindPlayoff: 'Mata-mata',
    scoringTitle: 'Sistema de pontos',
    scoringIntro:
      'Cada competição distribui os pontos do seu jeito; o sistema é dado, não está no código.',
    scoringRule: '{winner}-{loser} → {winnerPoints} e {loserPoints}',
    scoringFallback: 'Qualquer outro resultado: {winnerPoints} ao vencedor e {loserPoints} ao perdedor.',
    scoringForfeit: 'W.O.: {sets} com {points} por set.',
    tiebreakTitle: 'Critérios de desempate, em ordem',
    legs: 'Turnos',
    legsOne: 'Só ida',
    legsTwo: 'Ida e volta',

    // --- Fixture -------------------------------------------------------------
    matchday: 'Rodada {matchday}',
    fixtureEmpty:
      'Esta competição ainda não tem tabela de jogos. Dá para gerar a partir de uma fase ou lançar as partidas à mão.',
    generate: 'Gerar tabela de jogos',
    generating: 'Gerando…',
    generateHint:
      'Gera o calendário da fase escolhida. Gerar de novo NÃO desliga as partidas já jogadas.',
    generateDone: 'Tabela gerada: {count} partida(s).',
    thirdPlace: 'Com disputa de terceiro lugar',
    vs: 'x',
    noRival: 'A definir',
    winnerOf: 'Vencedor de {of}',
    loserOf: 'Perdedor de {of}',
    roundFinal: 'Final',
    roundSemi: 'Semifinal',
    roundQuarter: 'Quartas de final',
    roundOf16: 'Oitavas de final',
    roundNumber: 'Rodada {round}',
    matchNumber: '{round} {position}',
    thirdPlaceMatch: 'Terceiro lugar',
    statusScheduled: 'Marcada',
    statusLive: 'Em jogo',
    statusPlayed: 'Jogada',
    statusWalkover: 'W.O.',
    statusPostponed: 'Adiada',
    statusCancelled: 'Suspensa',
    originLog: 'do scouting',
    originManual: 'lançado à mão',
    originWalkover: 'por regulamento',
    resultFrom: 'Resultado {origin}',
    openMatch: 'Ver a partida',
    venue: 'Ginásio',
    colDate: 'Dia',
    colMatch: 'Partida',
    colResult: 'Resultado',
    colSets: 'Parciais',
    colStatusFixture: 'Situação',

    // --- Posiciones ----------------------------------------------------------
    standingsEmpty: 'Ainda não há fases com classificação nesta competição.',
    standingsIntro:
      'A classificação é calculada a partir das partidas lançadas: nunca é enviada à mão. Só contam as jogadas e os W.O.',
    computedWith: 'Calculada com {scoring}',
    matchesCounted: '{counted} de {total} partida(s) com resultado',
    colPos: '#',
    colTeam: 'Equipe',
    colPlayed: 'J',
    colWon: 'V',
    colLost: 'D',
    colSetsWon: 'SV',
    colSetsLost: 'SP',
    colSetRatio: 'Coef. sets',
    colPointsFor: 'PP',
    colPointsAgainst: 'PC',
    colPointRatio: 'Coef. pontos',
    colTablePoints: 'Pts',
    colAdjustment: 'Ajuste',
    tied: 'Empatada com a de cima',
    brokenBy: 'Desempate: {criterion}',
    adjustmentTitle: 'Ajuste de pontos: {points} ({reason})',
    tbPoints: 'pontos',
    tbMatchesWon: 'partidas vencidas',
    tbSetRatio: 'coeficiente de sets',
    tbSetDiff: 'saldo de sets',
    tbPointRatio: 'coeficiente de pontos',
    tbPointDiff: 'saldo de pontos',
    tbHeadToHead: 'confronto direto',
    tbSetsWon: 'sets vencidos',
    tbPointsScored: 'pontos marcados',

    // --- Enganche de partidos ------------------------------------------------
    linkTitle: 'Ligar partidas do arquivo',
    linkHint:
      'Partidas já lançadas que não pertencem a nenhuma rodada. Ao ligá-las, o resultado sai do log: a classificação se atualiza sozinha.',
    linkEmpty: 'Não sobram partidas soltas para ligar.',
    linkAction: 'Ligar {count} partida(s)',
    linkFilter: 'Filtrar pela competição declarada na partida',
    linkDone: '{linked} partida(s) ligada(s); {teams} equipe(s) criada(s).',
    linkSkipped: '{count} partida(s) não puderam ser ligadas.',
    selectAll: 'Selecionar todas',
    clearSelection: 'Limpar seleção',
  },

  competitionForm: {
    newCompetition: 'Nova competição',
    edit: 'Editar',
    remove: 'Excluir',
    editing: 'Editando {id}',
    savedNotice: 'Competição “{name}” salva.',
    deletedNotice: 'Competição “{name}” excluída.',
    confirmDelete:
      'Excluir a competição “{name}”? A tabela de jogos vai junto; as partidas já registradas continuam no arquivo.',
    noPermission:
      'Para criar ou editar competições é preciso a permissão {permission}, que hoje pertence ao perfil administrador.',

    identityTitle: 'Dados da competição',
    competitionId: 'Id',
    competitionIdHint: 'Vai na URL e liga a tabela de jogos. Não dá para mudar depois.',
    competitionIdPlaceholder: 'liga-a1-2026',
    name: 'Nome',
    namePlaceholder: 'Liga A1',
    season: 'Temporada',
    seasonPlaceholder: '2026 ou 2025/26',
    status: 'Situação',
    sportConfig: 'Regulamento',
    description: 'Descrição',

    teamsTitle: 'Equipes participantes',
    teamsHint:
      'Um clube por linha: “id, Nome, cabeça de chave”. Com uma coluna só, vale como nome e o id é proposto sozinho. A cabeça de chave define os confrontos do mata-mata.',
    teamsPlaceholder: 'bolivar, Club Bolívar, 1\nupcn, UPCN Vóley, 2\nCiudad Vóley',
    teamsCount: '{count} equipe(s) carregada(s).',

    scoringTitle: 'Sistema de pontos',
    scoringHint:
      'Como cada resultado distribui pontos. É dado da competição, não está no código: se esta liga dá 4 pontos por um 3-1, é mais uma linha.',
    preset: 'Modelo',
    presetCustom: 'Personalizado',
    presetFivb: 'FIVB (3-0 e 3-1 → 3; 3-2 → 2 e 1)',
    presetFivbBo3: 'Melhor de 3 (2-0 → 3; 2-1 → 2 e 1)',
    presetWinLoss21: '2 pontos por vitória, 1 por derrota',
    presetWin10: '1 ponto por vitória',
    scoringLabel: 'Rótulo',
    rules: 'Tabela de pontos',
    rulesHint:
      'Uma linha por resultado, sempre do ponto de vista do vencedor: “3-2 2 1” = quem vence por 3-2 soma 2 e quem perde 1.',
    rulesPlaceholder: '3-0 3 0\n3-1 3 0\n3-2 2 1',
    winPoints: 'Pontos do vencedor',
    lossPoints: 'Pontos do perdedor',
    fallbackHint:
      'Usados para os resultados que a tabela não lista (uma partida de três sets numa competição declarada como melhor de cinco, por exemplo).',
    forfeitTitle: 'W.O. / equipe ausente',
    forfeitSets: 'Sets do vencedor',
    forfeitLoserSets: 'Sets do perdedor',
    forfeitPointsPerSet: 'Parcial por set',
    forfeitWinnerPoints: 'Pontos do vencedor',
    forfeitWinnerPointsHint: 'Vazio = o que a tabela disser para esse resultado.',
    forfeitLoserPoints: 'Pontos do perdedor',

    tiebreakTitle: 'Critérios de desempate',
    tiebreakHint:
      'A ORDEM importa tanto quanto a lista: duas ligas com os mesmos critérios em ordem diferente montam tabelas diferentes.',
    tiebreakAdd: 'Adicionar critério',
    tiebreakUp: 'Subir',
    tiebreakDown: 'Descer',
    tiebreakRemove: 'Tirar',
    tiebreakRestore: 'Voltar à ordem FIVB',
    tiebreakEmptyHint: 'Sem critérios a tabela não sabe como ordenar duas equipes empatadas.',

    phasesTitle: 'Fases',
    phasesHint:
      'Uma competição é uma ou mais fases: regular, grupos ou mata-mata. A tabela de jogos é gerada por fase, na ficha.',
    phasesEmpty: 'Ainda não há fases. Sem pelo menos uma não dá para gerar a tabela de jogos.',
    addPhase: 'Adicionar fase',
    removePhase: 'Tirar fase',
    phaseNumber: 'Fase {number}',
    phaseId: 'Id da fase',
    phaseName: 'Nome',
    phaseNamePlaceholder: 'Fase regular',
    phaseKind: 'Tipo',
    phaseLegs: 'Turnos',
    phaseTeams: 'Equipes da fase',
    phaseTeamsHint:
      'Ids separados por vírgula. Vazio = todas as da competição, na ordem das cabeças de chave.',
    phaseGroups: 'Grupos',
    phaseGroupsHint: 'Um por linha: “Grupo A: bolivar, upcn”. Só na fase de grupos.',
    phasePreserved:
      'Esta fase tem configuração carregada por API que esta tela não edita ({detail}). Fica igual ao salvar.',
    phasePreservedScoring: 'sistema de pontos próprio',
    phasePreservedTiebreakers: 'desempates próprios',
    phasePreservedAdjustments: '{count} ajuste(s) de pontos',

    errors: {
      idRequired: 'Falta o id da competição.',
      idInvalid: 'O id só aceita letras, números, ponto, hífen e sublinhado.',
      nameRequired: 'Falta o nome da competição.',
      seasonTooLong: 'A temporada não pode passar de 40 caracteres.',
      descriptionTooLong: 'A descrição não pode passar de 1000 caracteres.',
      number: 'O campo “{field}” tem um valor inválido.',
      teamId: 'Linha {line} de equipes: falta o id.',
      teamIdInvalid:
        'Linha {line} de equipes: o id “{id}” só aceita letras, números, ponto, hífen e sublinhado.',
      teamName: 'Linha {line} de equipes: falta o nome do clube.',
      teamRepeated: 'Linha {line} de equipes: o id “{id}” está duas vezes.',
      teamSeed: 'Linha {line} de equipes: “{token}” não é uma cabeça de chave válida (1 a 256).',
      ruleShape:
        'Linha {line} da tabela de pontos: esperam-se quatro números (sets do vencedor, do perdedor, pontos de cada um).',
      ruleSets: 'Linha {line} da tabela de pontos: os sets têm que ser inteiros de 0 a 9.',
      ruleWinner:
        'Linha {line} da tabela de pontos: a linha se escreve do vencedor, então os sets dele têm que ser mais.',
      rulePoints: 'Linha {line} da tabela de pontos: os pontos não podem passar de 99.',
      ruleRepeated: 'Linha {line} da tabela de pontos: o resultado {result} já estava.',
      groupName: 'Linha {line} de grupos: falta o nome do grupo.',
      groupId: 'Linha {line} de grupos: de “{name}” não sai um id válido.',
      groupRepeated: 'Linha {line} de grupos: “{name}” está duas vezes.',
      phaseId: 'Fase {phase}: falta o id.',
      phaseIdInvalid: 'Fase {phase}: o id só aceita letras, números, ponto, hífen e sublinhado.',
      phaseName: 'Fase {id}: falta o nome.',
      phaseRepeated: 'A fase “{id}” está duas vezes.',
      phaseUnknownTeam:
        'Fase {phase}: usa a equipe “{id}”, que não está na lista de participantes.',
      tiebreakEmpty: 'É preciso pelo menos um critério de desempate.',
    },
  },

  import: {
    title: 'Importar histórico (.dvw)',
    navLabel: 'Importar',
    intro:
      'Escolha os .dvw de uma temporada inteira: eles são analisados primeiro e nada é gravado até você clicar em Importar. Os arquivos do DataVolley entram com elencos, escalações, rallies e sanções; o que o formato não permite traduzir aparece abaixo, linha por linha.',
    fileLabel: 'Arquivos .dvw (dá para escolher todos de uma vez)',
    chosen: '{count} arquivo(s) escolhidos',
    notDvw: '{count} não termina(m) em .dvw: vamos tentar assim mesmo.',
    noPermission: 'Seu perfil não pode importar histórico.',
    noPermissionHint:
      'A API exige a permissão {permission}, que a diretoria e o estatístico têm. A tela aparece inteira para você saber o que pedir.',
    noTeamsPermission: 'As partidas vão entrar, mas a base de equipes não é tocada.',
    noTeamsPermissionHint:
      'Preencher elencos exige {permission} (diretoria). Sem essa permissão o import carrega as partidas e deixa as equipes como estão, em vez de fazer pela metade sem avisar.',
    readingBatch: 'Analisando lote {batch} de {total}…',
    writingBatch: 'Importando lote {batch} de {total}…',
    previewCard: 'O que vai ser importado',
    resultCard: 'O que foi importado',
    willImport: 'Vão entrar {matches} partida(s) novas, com {events} eventos.',
    didImport: 'Entraram {matches} partida(s), com {events} eventos.',
    alreadyImported: '{count} já estavam importadas: não são tocadas (o mesmo arquivo dá sempre a mesma partida).',
    failedFiles: '{count} arquivo(s) não puderam ser lidos.',
    unmappedTotal: '{count} linha(s) entraram como comentário em vez de evento: estão abaixo.',
    teamsResult: 'Equipes: {created} criadas, {merged} atualizadas com jogadores novos.',
    importButton: 'Importar {count} partida(s)',
    nothingToImport: 'Não há nada novo para importar.',
    previewOnly: 'Até aqui nada foi gravado.',
    goToMatches: 'Ver as partidas importadas',
    unmappedCard: 'O que não deu para traduzir',
    unmappedIntro:
      'O importador não descarta nada: o que o modelo canônico não representa entra como comentário com a linha original, então o arquivo pode ser exportado de volta sem buracos. Isto é exatamente o que ficou assim.',
    unmappedNone: 'O arquivo inteiro virou eventos: não sobrou nenhuma linha.',
    kind_unknown_code: 'Código que o parser não reconhece (olhe o arquivo)',
    kind_green_code: 'Ponto sem detalhe da ação (código verde do DataVolley)',
    kind_setter_change: 'Troca de levantador (o modelo não tem esse evento)',
    kind_sanction_no_team: 'Cartão sem equipe (equipe de arbitragem)',
    teamsCard: 'Equipes que o histórico traz',
    teamsNone: 'Nenhum arquivo traz elenco.',
    teamState_created: 'vai ser criada',
    teamState_merged: 'ganha jogadores novos',
    teamState_unchanged: 'sem mudanças',
    teamState_skipped: 'não é tocada (falta permissão)',
    filesCard: 'Arquivo por arquivo',
    colCode: 'Código',
    colWhy: 'Por quê',
    colLines: 'Linhas',
    colFiles: 'Arquivos',
    colSample: 'Amostra',
    colTeam: 'Equipe',
    colNewPlayers: 'Jogadores novos',
    colFile: 'Arquivo',
    colMatch: 'Partida',
    colDate: 'Data',
    colSets: 'Sets',
    colEvents: 'Eventos',
    colUnmapped: 'Sem traduzir',
    colState: 'Estado',
    outcome_preview: 'pronta para importar',
    outcome_imported: 'importada',
    outcome_already_imported: 'já estava',
    outcome_failed: 'não deu para ler',
  },

  teams: {
    title: 'Equipes',
    newTeam: 'Nova equipe',
    editing: 'Editando {id}',
    loadingWhat: 'equipes',
    empty:
      'A base de equipes está vazia. Com «Nova equipe» o elenco é carregado uma única vez e depois a captura o reutiliza em cada partida.',
    name: 'Nome',
    namePlaceholder: 'Bolívar',
    teamId: 'Id (chave no arquivo)',
    teamIdPlaceholder: 'bolivar',
    code: 'Código (até 8)',
    codePlaceholder: 'BOL',
    coach: 'Técnico',
    roster: 'Elenco — uma linha por jogador: número, nome e função opcional ({roles})',
    rosterPlaceholder: '1 Pérez levantador\n14 Palacios ponteiro\n6 Gómez líbero',
    liberos: 'Líberos (camisas, separadas por vírgula)',
    liberosPlaceholder: '6, 12',
    savedNotice: 'Equipe «{name}» salva.',
    deletedNotice: 'Equipe «{name}» apagada.',
    confirmDelete: 'Apagar a equipe «{name}»? Não dá para desfazer.',
    colTeam: 'Equipe',
    colCode: 'Código',
    colCoach: 'Técnico',
    colPlayers: 'Jogadores',
    colLiberos: 'Líberos',
    colUpdated: 'Atualizado',
    colActions: 'Ações',
    edit: 'Editar',
    remove: 'Apagar',
    roles: {
      setter: 'levantador',
      outside: 'ponteiro',
      opposite: 'oposto',
      middle: 'central',
      libero: 'líbero',
      unknown: 'sem função',
    },
    errors: {
      idRequired: 'Falta o id da equipe (é a chave no arquivo).',
      idInvalid: 'O id só aceita letras, números, ponto, hífen e sublinhado.',
      nameRequired: 'Falta o nome da equipe.',
      codeTooLong: 'O código não pode ter mais de 8 caracteres.',
      badNumber: 'Linha {line}: «{token}» não é uma camisa válida (0 a 99).',
      repeatedNumber: 'Linha {line}: a camisa #{number} está repetida.',
      badLibero: '«{token}» não é uma camisa válida de líbero.',
      liberoNotInRoster: 'O líbero #{number} não está no elenco.',
    },
  },

  auth: {
    title: 'Entrar',
    intro: 'Entre com o usuário da federação para ver o arquivo com o seu perfil.',
    username: 'Usuário',
    usernamePlaceholder: 'nome.sobrenome',
    password: 'Senha',
    submit: 'Entrar',
    submitting: 'Entrando…',
    logout: 'Sair',
    loggingOut: 'Saindo…',
    missingFields: 'Preencha usuário e senha.',
    badCredentials: 'Usuário ou senha incorretos.',
    accountDisabled: 'A conta está desativada: peça a um administrador para reativá-la.',
    tooManyAttempts: 'Tentativas demais. Espere um minuto e tente de novo.',
    sessionExpired: 'Sua sessão venceu ou foi revogada. Entre de novo.',
    welcome: 'Você entrou como {name}.',
    alreadyIn: 'Você já está dentro como {name} ({role}).',
    goToMatches: 'Ir para Partidas',
    signedInAs: '{name} · {role}',
    since: 'Sessão iniciada em {date}.',
    anon: 'Sem sessão',
    anonTitle: 'Ninguém iniciou sessão neste navegador',
    login: 'Entrar',
    apiClosed:
      'A API da federação pede credenciais. Entre com o seu usuário ou carregue o token de compatibilidade em Configurações.',
    compatCard: 'Modo compatibilidade',
    compatHint:
      'Se em vez de usuário e senha você tem o token único da federação, carregue-o em Configurações. Continua funcionando para integrações, mas não deixa rastro de QUEM fez cada coisa: a auditoria registra como «token da federação».',
    compatLink: 'Ir para Configurações',
    viaSession: 'sessão',
    viaLegacyToken: 'token de compatibilidade',
    viaOpen: 'API aberta (dev)',
    viaLegacyTokenTitle:
      'Você está usando o Bearer manual de Configurações (modo compatibilidade): a API trata você como admin e a auditoria não sabe quem é.',
    viaOpenTitle: 'A API está em modo dev aberto (SCOUT_DEV_OPEN=1): não pede credenciais.',
    viaMatchCredential: 'credencial de partida',
    viaMatchCredentialTitle:
      'Você entrou com a credencial QR+PIN emitida pela mesa: vale só para esta partida e expira sozinha.',
    roles: {
      admin: 'admin',
      scout: 'estatístico',
      coach: 'técnico',
      viewer: 'direção/imprensa',
    },
  },

  users: {
    title: 'Usuários',
    navLabel: 'Usuários',
    intro:
      'O cadastro da federação: quem entra, com qual perfil e desde quando. Sem limite de usuários nem cobrança por assento.',
    loadingWhat: 'o cadastro',
    empty: 'O cadastro está vazio. Crie o primeiro usuário para largar o token compartilhado.',
    newUser: 'Novo usuário',
    editing: 'Editando «{username}»',
    username: 'Usuário (minúsculas, números, ponto, hífen e sublinhado)',
    name: 'Nome completo',
    role: 'Perfil',
    passwordNew: 'Senha (mínimo {min} caracteres)',
    passwordEdit: 'Senha nova (vazio = não muda)',
    passwordHint: 'Trocar a senha fecha TODAS as sessões abertas dessa pessoa.',
    disabled: 'Conta bloqueada (não pode entrar)',
    disabledHint: 'Bloquear fecha as sessões na hora; o usuário e o histórico ficam.',
    colUser: 'Usuário',
    colName: 'Nome',
    colRole: 'Perfil',
    colStatus: 'Estado',
    colCreated: 'Cadastro',
    colActions: 'Ações',
    statusActive: 'Ativo',
    statusDisabled: 'Bloqueado',
    you: 'você',
    edit: 'Editar',
    resetPassword: 'Redefinir senha',
    revoke: 'Fechar sessões',
    block: 'Bloquear',
    unblock: 'Desbloquear',
    remove: 'Excluir',
    working: '…',
    confirmDelete:
      'Excluir «{username}»? As sessões são fechadas e o usuário não pode ser recuperado.',
    confirmRevoke: 'Fechar todas as sessões de «{username}»? Vai precisar entrar de novo.',
    createdNotice: 'Usuário «{username}» criado.',
    savedNotice: 'Usuário «{username}» salvo.',
    deletedNotice: 'Usuário «{username}» excluído.',
    revokedNotice: 'As sessões de «{username}» foram fechadas.',
    blockedNotice: '«{username}» bloqueado e sessões fechadas.',
    unblockedNotice: '«{username}» desbloqueado.',
    revokeHint:
      'Fechar sessões bloqueia e desbloqueia a conta: é o único caminho que a API expõe hoje, então ficam duas entradas na auditoria.',
    revokeHalfway:
      'As sessões de «{username}» foram fechadas, mas a conta FICOU BLOQUEADA: desbloqueie pela lista ({reason}).',
    selfDeleteTitle: 'Você não pode excluir o seu próprio usuário.',
    forbidden: 'A administração do cadastro é só do perfil admin.',
    forbiddenHint:
      'Seu perfil é «{role}». O cadastro é de quem tem a permissão «{permission}»: peça à federação.',
    anon: 'Entre com um usuário admin para administrar o cadastro.',
    roleHintAdmin: 'admin — tudo, incluindo o cadastro e a auditoria.',
    roleHintScout: 'estatístico — carrega a partida e lê/exporta tudo.',
    roleHintCoach: 'técnico — lê tudo (incluindo o .dvw), não escreve eventos.',
    roleHintViewer: 'direção/imprensa — só os documentos já prontos.',
    errors: {
      usernameRequired: 'Falta o usuário (é a chave do cadastro e vai na URL).',
      usernameInvalid: 'O usuário só aceita minúsculas, números, ponto, hífen e sublinhado.',
      nameRequired: 'Falta o nome da pessoa.',
      passwordRequired: 'Ao criar um usuário a senha é obrigatória.',
      passwordShort: 'A senha precisa de pelo menos {min} caracteres.',
    },
  },

  settings: {
    title: 'Configurações',
    connectionCard: 'Conexão com a API',
    apiUrl: 'URL da API (vazio = mesma origem deste portal)',
    token: 'Token (Bearer) — modo compatibilidade. Vazio se você entrar com usuário.',
    tokenHint:
      'É o token único da federação (SCOUT_API_TOKEN), anterior aos usuários. Fica para integrações: se você iniciou sessão, ela manda e este campo é ignorado.',
    sessionCard: 'Sessão',
    sessionActive: 'Você está dentro como {name} ({role}).',
    sessionNone: 'Nenhuma sessão iniciada neste navegador.',
    sessionLogin: 'Entrar com usuário',
    sessionLogout: 'Sair',
    sessionUsingToken: 'Este navegador está usando o token de compatibilidade, não uma sessão.',
    testConnection: 'Testar conexão',
    testing: 'Testando…',
    testOk: 'API v{version} · {count} partida(s) acessíveis.',
    testBadToken: 'A API v{version} responde, mas recusou o token: confira o Authorization.',
    testPartial: 'A API v{version} responde (a listagem devolveu: {reason}).',
    savedHint: 'O teste usa a configuração JÁ SALVA. Se você mudou algo, salve primeiro.',
    themeCard: 'Design',
    themeLabel: 'Escolha o design',
    themeVar: 'Novo (VAR)',
    themeClasico: 'Clássico',
    languageCard: 'Idioma',
    languageLabel: 'Idioma da interface',
    languageHint: 'Aplica na hora e fica salvo no localStorage deste navegador.',
    efficiencyCard: 'Convenção de estatística',
    efficiencyIntro:
      'Cada comissão técnica calcula eficiência e positividade com sua convenção. A escolhida aqui vale em TODAS as abas de estatística, no relatório imprimível e no acumulado da temporada.',
    efficiencyLabel: 'Fórmulas de eficiência',
    efficiencyHint:
      'Aplica na hora, como o idioma. Não muda o log nem nenhuma contagem: só com qual fórmula se dividem as avaliações já registradas.',
    replayCard: 'Servidor de replay (VAR)',
    replayIntro:
      'É o servidor de vídeo DO GINÁSIO, na LAN. O clipe sai dali direto para este navegador, sem passar pelo API.',
    replayUrl: 'URL do servidor de replay (vazio = sem VAR neste navegador)',
    replayToken: 'Token do servidor de replay',
    replayHint:
      'Se o portal for servido por HTTPS e o VAR for HTTP na LAN, o navegador vai bloquear o pedido por conteúdo misto: no ginásio abra o portal por HTTP, ou publique o VAR com TLS.',
    howCard: 'Como funciona',
    howLocal: 'As configurações vivem no localStorage deste navegador; não são sincronizadas.',
    howLiveBefore: 'O placar ao vivo é lido com ',
    howLiveMiddle: ' em modo streaming (não com',
    howLiveAfter: ') justamente para poder mandar o header de autorização.',
    howCorsBefore: 'Se a API estiver em outro domínio, ela precisa permitir esta origem por CORS (',
    howCorsAfter: ').',
  },

  api: {
    badRequest: 'A API recusou o pedido (400).',
    unauthorized: 'A API pede autenticação: confira o token em Configurações.',
    forbidden: 'Seu perfil não tem permissão para isso (403). Trocar o token em Configurações não resolve: quem libera é a federação.',
    forbiddenPermission:
      'Seu perfil não tem a permissão «{permission}» que a API exige (403). Quem libera é a federação; Configurações não resolve.',
    notFound: 'A API não encontrou o que foi pedido (404).',
    conflict: 'A partida está incompleta (409).',
    unprocessable: 'O regulamento da partida é desconhecido (422).',
    notImplemented: 'A API ainda não consegue gerar isso (módulo pendente).',
    serverError: 'Erro do servidor ({status}).',
    unexpected: 'Resposta inesperada da API ({status}).',
    noFetch: 'Este ambiente não tem fetch.',
    networkError: 'Não foi possível conectar com a API ({reason}).',
    notJson: 'A API respondeu algo que não é JSON.',
  },

  /** PORTAL PÚBLICO (`/{slug}`): a cara pública da federação. */
  publicPortal: {
    title: 'Portal público',
    today: 'Hoje',
    todayHint: 'Os jogos de hoje, ao vivo, sem recarregar a página.',
    todaySection: 'Jogos de hoje',
    upcoming: 'Próximos jogos',
    recent: 'Últimos resultados',
    competitions: 'Competições',
    results: 'Resultados',
    resultsHint: 'O arquivo completo da temporada, do mais novo ao mais antigo.',
    competition: 'Competição',
    match: 'Jogo',
    fixture: 'Tabela de jogos',
    standings: 'Classificação',
    evolution: 'Evolução do placar',
    setsTable: 'Parciais',
    pointsByReason: 'Como os pontos foram ganhos',
    reason: 'Motivo',
    set: 'Set',
    total: 'Total',
    inPlay: 'em jogo',
    statusLive: 'Ao vivo',
    statusWalkover: 'W.O.',
    statusPostponed: 'Adiado',
    statusCancelled: 'Cancelado',
    matchday: 'Rodada {matchday}',
    tbd: 'A definir',
    seeAll: 'Ver todos',
    teamsCount: '{count} equipes',
    matchesCount: '{count} jogos',
    filterCompetition: 'Competição:',
    allCompetitions: 'Todas',
    noDate: 'Sem data',
    noMatchesToday: 'Não há jogos marcados para hoje.',
    noCompetitions: 'Ainda não há competições publicadas.',
    noResults: 'Ainda não há resultados.',
    noFixture: 'O calendário desta competição ainda não foi publicado.',
    noStandings: 'Esta competição ainda não tem classificação.',
    noSets: 'O jogo ainda não começou.',
    noPoints: 'Nenhum ponto foi jogado ainda.',
    notStarted: 'O jogo está marcado e ainda não começou.',
    pendingMatches: 'Faltam {count} jogos desta fase.',
    truncated: 'Mostrando os resultados mais recentes.',
    position: 'Pos',
    team: 'Equipe',
    points: 'Pts',
    played: 'J',
    won: 'V',
    lost: 'D',
    setsFor: 'Sets',
    setRatio: 'Razão de sets',
    pointRatio: 'Razão de pontos',
    updated: 'Atualizado {ago}',
    agoSeconds: 'há {seconds} s',
    agoMinutes: 'há {minutes} min',
    retrying: 'tentando de novo',
    stale: 'Não foi possível atualizar: você está vendo a última informação recebida.',
    loadFailed: 'Não foi possível carregar as informações. O portal tenta de novo sozinho.',
    notFoundTitle: 'Não há portal aqui',
    notFoundBody: 'Não existe portal público em /{slug}, ou a federação o desativou.',
    unknownRoute: 'Este endereço não existe no portal.',
    staffLink: 'Acesso da federação',
    widgetHint: 'Para uma transmissão:',
    widgetLink: 'placar para OBS',
    widgetStale: 'Placar sem atualização',
    widgetLabel: '{home} {setsHome} - {setsAway} {away}, parcial {scoreHome} a {scoreAway}',
  },

  describe: {
    unknownPlayer: 's/n',
    matchCreated: 'Partida criada: {name}',
    setStarted: 'Começa o set {set}',
    setEnded: 'Fim do set {set}: {home}–{away} ({team})',
    matchEnded: 'Fim da partida: vence {team} {setsHome}–{setsAway}',
    lineupSet: 'Formação de {team}: {rotation}',
    touch: '{team} · {skill} {evaluation} — {player}',
    touchZones: '{team} · {skill} {evaluation} — {player} ({startZone}→{endZone})',
    point: 'Ponto {team} de {reason} — {home}–{away}',
    substitution: 'Substituição em {team}: entra #{playerIn} por #{playerOut}',
    substitutionLibero: 'Substituição em {team}: entra #{playerIn} por #{playerOut} (líbero)',
    timeoutTechnical: 'Tempo técnico de {team}',
    timeoutRegular: 'Pedido de tempo de {team}',
    sanction: 'Sanção ({card}) para {team}',
    comment: 'Nota: {text}',
    undo: 'Desfazer',
    correction: 'Correção',
    officialsSet: 'Equipe de arbitragem registrada ({count})',
    toss: 'Sorteio: {team} vence e escolhe {choice}',
    tossDecider: 'Sorteio do set decisivo: {team} vence e escolhe {choice}',
    lineupApproved: 'Escalação de {team} aprovada (set {set})',
    rallyStarted: 'Saque de {team} · #{server}',
    challengeRequested: 'Challenge pedido por {team}',
    challengeOfficial: 'Revisão de ofício do 1º árbitro',
    challengeResolved: 'Challenge resolvido: {outcome} ({action})',
    replayBookmark: 'Marcação de vídeo: {label}',
    signature: 'Assinatura de {role}: {name}',
    sheetClosed: 'Súmula encerrada ({outcome})',
  },

  court: {
    net: 'REDE',
    libero: 'líbero',
    captain: 'capitão',
    serving: 'Saca',
    playerTitle: '#{number} na P{position}',
    roles: {
      setter: 'LEV',
      outside: 'PON',
      opposite: 'OPO',
      middle: 'CEN',
      libero: 'LIB',
    },
  },

  mesaPanel: {
    autoRefresh: 'Atualizado',
    autoRefreshTitle: 'A mesa se atualiza sozinha a cada poucos segundos enquanto a súmula está aberta.',
    scoreLabel: 'Placar da partida',
    lineupsTitle: 'Formações em quadra · set {set}',
    courtLabel: 'Quadra: formações de {home} e {away}',
    flowLabel: 'Fluxo do desafio',
    flow: {
      request: 'Pedido',
      reason: 'Motivo',
      outcome: 'Resultado',
      score: 'Placar',
      confirm: 'Registrar',
    },
    flowHint: {
      request: 'Buzina do banco ou de ofício',
      reason: 'A falta desafiada',
      outcome: 'Um dos cinco do VCS',
      score: 'Como fica o ponto',
      confirm: 'Um só lote na súmula',
    },
    reviewTitle: 'Revisão',
    keeps: 'Mantém o desafio',
    loses: 'Perde o desafio',
    confirmNeeds: 'Escolha o resultado e a ação no placar para habilitar o registro.',
    windowTitle: 'Janela de {seconds} s',
    windowOpen: 'Restam {seconds} s para pedir desafio',
    windowClosed: 'Fechada: o último ponto não pode mais ser desafiado',
    windowReview: 'Revisão em andamento',
    windowNone: 'Ainda não há ponto para desafiar',
    windowHint: 'Art. 7 · contado desde que esta tela viu o ponto',
    qrLabel: 'Código QR da credencial de {holder}',
    showQr: 'Mostrar QR',
    hideQr: 'Ocultar QR',
    qrAgainHint: 'Escaneie com a câmera do tablet: abre o console com esta função. O PIN não é mostrado de novo.',
    qrElsewhere: 'O QR só pode ser mostrado de novo no notebook que emitiu a credencial.',
  },

  benchPanel: {
    lineupHint: 'Montar a do próximo set',
    state: {
      PENDING: 'Pendente',
      ACCEPTED: 'Aceito',
      REJECTED: 'Rejeitado',
      CANCELLED: 'Cancelado',
    },
  },

  matchViews: {
    kpiPoints: 'Pontos ({byPlayer} com autor)',
    kpiSideout: 'Side-out',
    kpiBreak: 'Break',
    kpiAttack: 'Ataque Ef%',
    kpiReception: 'Recepção +',
    attacksCount: '{count} ataques',
    zoneSub: '{count} · Ef {eff}',
    setterCourtTitle: 'Distribuição por zona de saída',
    setterCourtAll: 'Todas',
    setterCourtGrade: 'R{grade}',
    setterCourtLabel: 'Quadra: distribuição do levantador de {team} — {what}',
    setterCourtOther:
      '{count} ataques sem zona de saída 1–6 (combinações sem zona ou sem codificar): estão na tabela, não na quadra.',
    setterZoneTitle: 'Zona {zone}: {count} ataques ({pct}), eficiência {eff}',
    rotCourtSideout: 'Side-out por rodízio',
    rotCourtBreak: 'Break por rodízio',
    rotCourtLabel: 'Quadra: {what} de {team}',
    rotCourtHint:
      'Cada zona é um rodízio: R1 é o levantador na zona 1 (sacando), R2 na zona 2, e assim por diante. A cor vai do pior ao melhor do time.',
    rotSub: '{won}/{total}',
    zonesCourtTitle: 'Na quadra',
    zonesCourtLabel: 'Quadra: mapa de {skill} de {team}',
    zonesCourtHint:
      'Embaixo, a quadra própria: de onde saiu, pintada por eficiência. Em cima, a adversária: para onde foi, pintada por quantidade. Toque uma zona própria para ver só o que saiu dali.',
    zonesCourtHintReception:
      'Embaixo, a quadra própria: onde foi recebido, pintada por eficiência. Em cima, a adversária: de onde veio o saque. Toque uma zona própria para ver só essas recepções.',
    zonesCount: '{count} toques',
    zonesOwnTitle: 'Zona {zone}: {count} toques, eficiência {eff}',
    zonesOtherTitle: 'Zona {zone}: {count} toques',
    zonesFiltering: 'Filtrando zona {zone}',
    zonesClear: 'Ver todas',
    zonesNoFilter: 'Todas as zonas',
    zonesOther: '{count} toques sem zona 1–6: estão na matriz, não na quadra.',
    zonesMatrixTitle: 'Matriz saída × chegada',
  },
};
